/**
 * Demo State Manager Service
 * 
 * Handles persistent storage, history tracking, and state recovery for demo sessions.
 * Uses localStorage/sessionStorage with fallback to memory storage.
 * Provides recovery mechanisms and multi-tab synchronization.
 */

import {
  STORAGE_CONFIG,
  STATE_PERSISTENCE_CONFIG,
  HISTORY_TRACKING_CONFIG,
  PROGRESS_TRACKING_CONFIG,
  COMPLETION_TRACKING_CONFIG,
  STATE_RECOVERY_CONFIG,
  STORAGE_HEALTH_CONFIG,
  STATE_EXPORT_CONFIG,
  STATE_SYNC_CONFIG,
  STATE_MANAGEMENT_ERRORS,
  STATE_DEBUG_CONFIG,
} from '../constants';

/**
 * DemoStateManager
 * Singleton service for demo state persistence and recovery
 */
class DemoStateManager {
  constructor() {
    this.memoryStorage = new Map(); // Fallback storage
    this.currentStorage = null; // Active storage mechanism
    this.isInitialized = false;
    this.cleanupIntervals = [];
    this.syncIntervals = [];

    this._initializeStorage();
  }

  /**
   * Initialize storage mechanism
   * @private
   */
  _initializeStorage() {
    try {
      // Try primary storage
      if (this._isStorageAvailable(STORAGE_CONFIG.PRIMARY_STORAGE)) {
        this.currentStorage = STORAGE_CONFIG.PRIMARY_STORAGE;
      } else if (this._isStorageAvailable(STORAGE_CONFIG.FALLBACK_STORAGE)) {
        // Fallback to secondary storage
        this.currentStorage = STORAGE_CONFIG.FALLBACK_STORAGE;
        console.warn(
          `[DemoStateManager] ${STORAGE_CONFIG.PRIMARY_STORAGE} unavailable, ` +
          `using ${STORAGE_CONFIG.FALLBACK_STORAGE}`
        );
      } else {
        // Fallback to memory
        this.currentStorage = 'memory';
        console.warn('[DemoStateManager] Storage unavailable, using memory storage');
      }

      this.isInitialized = true;

      // Setup periodic cleanup
      if (STORAGE_HEALTH_CONFIG.CLEANUP_ENABLED) {
        this._setupPeriodicCleanup();
      }

      // Setup state sync
      if (STATE_SYNC_CONFIG.SYNC_ENABLED) {
        this._setupStateSynchronization();
      }
    } catch (error) {
      console.error('[DemoStateManager] Initialization failed:', error);
      this.currentStorage = 'memory';
    }
  }

  /**
   * Check if storage is available
   * @private
   * @param {string} storageType - 'localStorage', 'sessionStorage', or 'memory'
   * @returns {boolean}
   */
  _isStorageAvailable(storageType) {
    if (storageType === 'memory') return true;

    try {
      const test = '__storage_test__';
      const storage = window[storageType];
      storage.setItem(test, test);
      storage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the active storage object
   * @private
   * @returns {Storage|Map}
   */
  _getStorage() {
    if (this.currentStorage === 'memory') {
      return this.memoryStorage;
    }
    return window[this.currentStorage];
  }

  /**
   * Save demo state to storage
   * @param {Object} state - State object to save
   * @async
   */
  async saveState(state) {
    try {
      if (!state || typeof state !== 'object') {
        throw new Error('Invalid state object');
      }

      const storage = this._getStorage();
      const stateData = {
        ...state,
        savedAt: Date.now(),
        version: 1,
      };

      const key = this._getStorageKey(STORAGE_CONFIG.STATE_KEY);

      if (this.currentStorage === 'memory') {
        storage.set(key, stateData);
      } else {
        storage.setItem(key, JSON.stringify(stateData));
      }

      if (STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoStateManager] State saved:', stateData);
      }

      // Save progress if persistence enabled
      if (STATE_PERSISTENCE_CONFIG.PERSIST_COMPLETED_STEPS) {
        await this._saveProgress(state);
      }

      return true;
    } catch (error) {
      this._handleStorageError(error, STATE_MANAGEMENT_ERRORS.STORAGE_UNAVAILABLE);
      return false;
    }
  }

  /**
   * Load demo state from storage
   * @async
   * @returns {Promise<Object|null>} - Loaded state or null
   */
  async loadState() {
    try {
      const storage = this._getStorage();
      const key = this._getStorageKey(STORAGE_CONFIG.STATE_KEY);

      let stateData = null;

      if (this.currentStorage === 'memory') {
        stateData = storage.get(key);
      } else {
        const stored = storage.getItem(key);
        stateData = stored ? JSON.parse(stored) : null;
      }

      if (!stateData) {
        return null;
      }

      // Validate state data
      if (STATE_RECOVERY_CONFIG.VALIDATE_RECOVERED_STATE) {
        if (!this._validateStateData(stateData)) {
          console.warn('[DemoStateManager] Invalid state data, attempting recovery');
          return null;
        }
      }

      if (STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoStateManager] State loaded:', stateData);
      }

      return stateData;
    } catch (error) {
      console.error('[DemoStateManager] Failed to load state:', error);
      return null;
    }
  }

  /**
   * Record a history entry
   * @param {string} action - Action type
   * @param {Object} context - Action context
   * @async
   */
  async recordHistoryEntry(action, context = {}) {
    try {
      if (!HISTORY_TRACKING_CONFIG.TRACKING_ENABLED) {
        return;
      }

      const entry = {
        timestamp: Date.now(),
        action,
        context,
        ...this._captureEntryFields(),
      };

      const storage = this._getStorage();
      const historyKey = this._getStorageKey(STORAGE_CONFIG.HISTORY_KEY);

      let history = [];
      if (this.currentStorage === 'memory') {
        history = storage.get(historyKey) || [];
      } else {
        const stored = storage.getItem(historyKey);
        history = stored ? JSON.parse(stored) : [];
      }

      // Add new entry
      history.push(entry);

      // Trim history to max size
      if (history.length > HISTORY_TRACKING_CONFIG.MAX_HISTORY_ENTRIES) {
        history = history.slice(-HISTORY_TRACKING_CONFIG.MAX_HISTORY_ENTRIES);
      }

      // Save updated history
      if (this.currentStorage === 'memory') {
        storage.set(historyKey, history);
      } else {
        storage.setItem(historyKey, JSON.stringify(history));
      }

      if (STATE_DEBUG_CONFIG.LOG_HISTORY_OPERATIONS) {
        console.log('[DemoStateManager] History entry recorded:', entry);
      }
    } catch (error) {
      console.error('[DemoStateManager] Failed to record history:', error);
    }
  }

  /**
   * Get history entries
   * @param {Object} options - Filter options
   * @returns {Array<Object>}
   */
  getHistory(options = {}) {
    try {
      const storage = this._getStorage();
      const historyKey = this._getStorageKey(STORAGE_CONFIG.HISTORY_KEY);

      let history = [];
      if (this.currentStorage === 'memory') {
        history = storage.get(historyKey) || [];
      } else {
        const stored = storage.getItem(historyKey);
        history = stored ? JSON.parse(stored) : [];
      }

      // Filter by action if specified
      if (options.action) {
        history = history.filter((entry) => entry.action === options.action);
      }

      // Filter by time range if specified
      if (options.since) {
        history = history.filter((entry) => entry.timestamp >= options.since);
      }

      return history;
    } catch (error) {
      console.error('[DemoStateManager] Failed to get history:', error);
      return [];
    }
  }

  /**
   * Save progress information
   * @private
   * @param {Object} state - Current state
   * @async
   */
  async _saveProgress(state) {
    try {
      const storage = this._getStorage();
      const progressKey = this._getStorageKey(STORAGE_CONFIG.PROGRESS_KEY);

      const progress = {
        currentStepIndex: state.currentStepIndex,
        completionPercentage: state.completionPercentage || 0,
        stepsCompleted: state.completedSteps?.length || 0,
        stepsRemaining: state.demoSteps?.length - (state.currentStepIndex + 1) || 0,
        estimatedTimeRemaining: this._calculateTimeRemaining(state),
        lastUpdated: Date.now(),
      };

      if (this.currentStorage === 'memory') {
        storage.set(progressKey, progress);
      } else {
        storage.setItem(progressKey, JSON.stringify(progress));
      }

      if (STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoStateManager] Progress saved:', progress);
      }
    } catch (error) {
      console.error('[DemoStateManager] Failed to save progress:', error);
    }
  }

  /**
   * Calculate estimated time remaining
   * @private
   * @param {Object} state - Current state
   * @returns {number} - Milliseconds
   */
  _calculateTimeRemaining(state) {
    if (!PROGRESS_TRACKING_CONFIG.ENABLE_TIME_ESTIMATION) {
      return 0;
    }

    const totalSteps = state.demoSteps?.length || 0;
    const remainingSteps = totalSteps - (state.currentStepIndex + 1);
    return remainingSteps * PROGRESS_TRACKING_CONFIG.BASE_STEP_DURATION_MS;
  }

  /**
   * Save completion status
   * @param {string} status - Completion status
   * @param {Object} metadata - Additional metadata
   * @async
   */
  async saveCompletionStatus(status, metadata = {}) {
    try {
      const storage = this._getStorage();
      const completionKey = this._getStorageKey(STORAGE_CONFIG.COMPLETION_KEY);

      const completion = {
        status,
        completedAt: status === 'completed' ? Date.now() : null,
        metadata,
        completionCount: this._getCompletionCount() + (status === 'completed' ? 1 : 0),
      };

      if (this.currentStorage === 'memory') {
        storage.set(completionKey, completion);
      } else {
        storage.setItem(completionKey, JSON.stringify(completion));
      }

      if (STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoStateManager] Completion status saved:', completion);
      }

      // Record history entry
      await this.recordHistoryEntry('completion-status-changed', { status, metadata });
    } catch (error) {
      console.error('[DemoStateManager] Failed to save completion status:', error);
    }
  }

  /**
   * Get completion count
   * @private
   * @returns {number}
   */
  _getCompletionCount() {
    try {
      const storage = this._getStorage();
      const completionKey = this._getStorageKey(STORAGE_CONFIG.COMPLETION_KEY);

      let completion = null;
      if (this.currentStorage === 'memory') {
        completion = storage.get(completionKey);
      } else {
        const stored = storage.getItem(completionKey);
        completion = stored ? JSON.parse(stored) : null;
      }

      return completion?.completionCount || 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Capture fields for history entry
   * @private
   * @returns {Object}
   */
  _captureEntryFields() {
    const fields = {};

    if (HISTORY_TRACKING_CONFIG.ENTRY_FIELDS.timestamp) {
      fields.timestamp = Date.now();
    }

    return fields;
  }

  /**
   * Validate state data structure
   * @private
   * @param {Object} state - State to validate
   * @returns {boolean}
   */
  _validateStateData(state) {
    if (!state || typeof state !== 'object') {
      return false;
    }

    // Check required fields
    if (typeof state.currentStepIndex !== 'number') {
      return false;
    }

    // Check data types
    if (state.demoSteps && !Array.isArray(state.demoSteps)) {
      return false;
    }

    return true;
  }

  /**
   * Get recovery strategies and attempt recovery
   * @async
   * @returns {Promise<Object|null>}
   */
  async attemptStateRecovery() {
    try {
      if (!STATE_RECOVERY_CONFIG.AUTO_RECOVERY_ENABLED) {
        return null;
      }

      const strategies = STATE_RECOVERY_CONFIG.RECOVERY_STRATEGIES;

      for (const strategy of strategies) {
        let recoveredState = null;

        if (strategy === 'RESTORE_LAST_SESSION') {
          recoveredState = await this.loadState();
        } else if (strategy === 'RESTORE_FIRST_INCOMPLETE') {
          recoveredState = await this._findFirstIncompleteStep();
        } else if (strategy === 'START_FROM_BEGINNING') {
          recoveredState = null; // Let caller handle
        }

        if (recoveredState) {
          if (STATE_DEBUG_CONFIG.LOG_RECOVERY_PROCESS) {
            console.log(`[DemoStateManager] State recovered using ${strategy}:`, recoveredState);
          }
          return recoveredState;
        }
      }

      return null;
    } catch (error) {
      console.error('[DemoStateManager] State recovery failed:', error);
      return null;
    }
  }

  /**
   * Find first incomplete step
   * @private
   * @async
   * @returns {Promise<Object|null>}
   */
  async _findFirstIncompleteStep() {
    const state = await this.loadState();
    if (!state || !Array.isArray(state.completedSteps)) {
      return null;
    }

    // Find first step not in completed list
    for (let i = 0; i < state.demoSteps.length; i++) {
      if (!state.completedSteps.includes(i)) {
        return {
          ...state,
          currentStepIndex: i,
        };
      }
    }

    return null;
  }

  /**
   * Get storage key with prefix
   * @private
   * @param {string} key - Key name
   * @returns {string}
   */
  _getStorageKey(key) {
    return `${STORAGE_CONFIG.STORAGE_PREFIX}${key}`;
  }

  /**
   * Clear all demo state from storage
   * @async
   */
  async clearAllState() {
    try {
      const storage = this._getStorage();
      const keysToRemove = [
        STORAGE_CONFIG.STATE_KEY,
        STORAGE_CONFIG.HISTORY_KEY,
        STORAGE_CONFIG.PROGRESS_KEY,
        STORAGE_CONFIG.COMPLETION_KEY,
        STORAGE_CONFIG.INTERACTIONS_KEY,
      ];

      keysToRemove.forEach((key) => {
        const fullKey = this._getStorageKey(key);
        if (this.currentStorage === 'memory') {
          storage.delete(fullKey);
        } else {
          storage.removeItem(fullKey);
        }
      });

      if (STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoStateManager] All state cleared');
      }

      await this.recordHistoryEntry('state-cleared', {});
    } catch (error) {
      console.error('[DemoStateManager] Failed to clear state:', error);
    }
  }

  /**
   * Setup periodic storage cleanup
   * @private
   */
  _setupPeriodicCleanup() {
    const interval = setInterval(() => {
      this._cleanupOldData();
    }, STORAGE_HEALTH_CONFIG.CLEANUP_INTERVAL_MS);

    this.cleanupIntervals.push(interval);

    if (STATE_DEBUG_CONFIG.LOG_CLEANUP_ACTIONS) {
      console.log('[DemoStateManager] Periodic cleanup started');
    }
  }

  /**
   * Cleanup old/expired data
   * @private
   * @async
   */
  async _cleanupOldData() {
    try {
      const storage = this._getStorage();
      const now = Date.now();
      const expirationMs = STORAGE_CONFIG.STORAGE_PREFIX 
        ? STORAGE_HEALTH_CONFIG.CLEANUP_STATES_OLDER_THAN_DAYS * 24 * 60 * 60 * 1000
        : 0;

      // Get current state
      const stateKey = this._getStorageKey(STORAGE_CONFIG.STATE_KEY);
      let state = null;

      if (this.currentStorage === 'memory') {
        state = storage.get(stateKey);
      } else {
        const stored = storage.getItem(stateKey);
        state = stored ? JSON.parse(stored) : null;
      }

      // Remove expired states
      if (STORAGE_HEALTH_CONFIG.CLEANUP_EXPIRED_STATES && state && state.savedAt) {
        if (now - state.savedAt > expirationMs) {
          const fullKey = this._getStorageKey(STORAGE_CONFIG.STATE_KEY);
          if (this.currentStorage === 'memory') {
            storage.delete(fullKey);
          } else {
            storage.removeItem(fullKey);
          }

          if (STATE_DEBUG_CONFIG.LOG_CLEANUP_ACTIONS) {
            console.log('[DemoStateManager] Expired state removed');
          }
        }
      }

      // Cleanup old history entries
      if (STORAGE_HEALTH_CONFIG.CLEANUP_OLD_HISTORY) {
        await this._cleanupOldHistory();
      }
    } catch (error) {
      console.error('[DemoStateManager] Cleanup failed:', error);
    }
  }

  /**
   * Cleanup old history entries
   * @private
   * @async
   */
  async _cleanupOldHistory() {
    try {
      const storage = this._getStorage();
      const historyKey = this._getStorageKey(STORAGE_CONFIG.HISTORY_KEY);
      const cutoffTime =
        Date.now() - 
        STORAGE_HEALTH_CONFIG.CLEANUP_HISTORY_OLDER_THAN_DAYS * 24 * 60 * 60 * 1000;

      let history = [];
      if (this.currentStorage === 'memory') {
        history = storage.get(historyKey) || [];
      } else {
        const stored = storage.getItem(historyKey);
        history = stored ? JSON.parse(stored) : [];
      }

      const filteredHistory = history.filter((entry) => entry.timestamp > cutoffTime);

      if (filteredHistory.length < history.length) {
        if (this.currentStorage === 'memory') {
          storage.set(historyKey, filteredHistory);
        } else {
          storage.setItem(historyKey, JSON.stringify(filteredHistory));
        }

        if (STATE_DEBUG_CONFIG.LOG_CLEANUP_ACTIONS) {
          console.log(
            `[DemoStateManager] Cleaned up ${history.length - filteredHistory.length} old history entries`
          );
        }
      }
    } catch (error) {
      console.error('[DemoStateManager] History cleanup failed:', error);
    }
  }

  /**
   * Setup state synchronization across tabs
   * @private
   */
  _setupStateSynchronization() {
    if (this.currentStorage === 'memory') {
      return; // No cross-tab sync for memory storage
    }

    // Listen for storage events (changes in other tabs)
    if (STATE_SYNC_CONFIG.LISTEN_TO_STORAGE_EVENTS) {
      window.addEventListener('storage', (event) => {
        this._handleStorageEvent(event);
      });
    }

    // Listen for visibility change to sync on regain focus
    if (STATE_SYNC_CONFIG.SYNC_ON_VISIBILITY_CHANGE) {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this._syncStateWithStorage();
        }
      });
    }

    if (STATE_DEBUG_CONFIG.DEBUG_ENABLED) {
      console.log('[DemoStateManager] State synchronization enabled');
    }
  }

  /**
   * Handle storage events from other tabs
   * @private
   * @param {StorageEvent} event - Storage event
   */
  _handleStorageEvent(event) {
    if (!event.key || !event.key.startsWith(STORAGE_CONFIG.STORAGE_PREFIX)) {
      return;
    }

    if (STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
      console.log('[DemoStateManager] Storage changed in another tab:', event.key);
    }

    // Emit event for UI to react
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent('demo-state-changed', {
          detail: { key: event.key, newValue: event.newValue },
        })
      );
    }
  }

  /**
   * Sync state with storage
   * @private
   * @async
   */
  async _syncStateWithStorage() {
    try {
      // Reload state from storage to ensure sync with other tabs
      const state = await this.loadState();
      
      if (state && STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
        console.log('[DemoStateManager] State synced with storage');
      }

      return state;
    } catch (error) {
      console.error('[DemoStateManager] State sync failed:', error);
      return null;
    }
  }

  /**
   * Handle storage errors
   * @private
   * @param {Error} error - Error object
   * @param {Object} errorInfo - Error info object
   */
  _handleStorageError(error, errorInfo) {
    console.error(`[DemoStateManager] ${errorInfo.message}:`, error);

    // Attempt recovery
    if (errorInfo.recovery === 'FALLBACK_TO_MEMORY') {
      if (this.currentStorage !== 'memory') {
        this.currentStorage = 'memory';
        console.warn('[DemoStateManager] Switched to memory storage');
      }
    }
  }

  /**
   * Cleanup resources
   */
  destroy() {
    // Clear all intervals
    this.cleanupIntervals.forEach((interval) => clearInterval(interval));
    this.syncIntervals.forEach((interval) => clearInterval(interval));
    this.cleanupIntervals = [];
    this.syncIntervals = [];

    if (STATE_DEBUG_CONFIG.LOG_STATE_CHANGES) {
      console.log('[DemoStateManager] Cleanup complete');
    }
  }
}

// Singleton instance
let stateManagerInstance = null;

/**
 * Get or create DemoStateManager instance
 * @returns {DemoStateManager}
 */
export function getDemoStateManager() {
  if (!stateManagerInstance) {
    stateManagerInstance = new DemoStateManager();
  }
  return stateManagerInstance;
}

/**
 * Reset DemoStateManager instance (for testing)
 */
export function resetDemoStateManager() {
  if (stateManagerInstance) {
    stateManagerInstance.destroy();
  }
  stateManagerInstance = null;
}

export default DemoStateManager;
